<template>
    <div v-if="map && floor && floor.id" class="map-container" :style="containerStyle" :inert="preview" :class="containerClass">
        <e-interactive-svg ref="svg" :real-width="floor.realWidth" :real-height="floor.realHeight" :camera="camera" init-with-camera
                           :show-scale-legend="showScaleLegend" :background-image="floor.imagePath" :opacity="map.style.background?.opacity"
                           @item-moved="$emit(`item-moved`, $event)" :item-selected="itemSelected" @unselect="$emit('unselect')"
                           @dragging="$emit('dragging', $event)"
                           @mousemove="$emit('mousemove', $event)" @background-click="$emit('background-click', $event)">
            <template #defs v-once>
                <slot name="defs"></slot>
                <linearGradient id="iconBG" x1="0" x2="0" y1="0" y2="1">
                    <template v-if="map.style.icons?.variant === `default`">
                        <stop offset="0%" stop-color="#5fadff"/>
                        <stop offset="100%" stop-color="#1b64e5"/>
                    </template>
                    <template v-else-if="map.style.icons?.variant === `blue`">
                        <stop offset="0%" stop-color="#5fadff"/>
                        <stop offset="100%" stop-color="#1b64e5"/>
                    </template>
                    <template v-else-if="map.style.icons?.variant === `gray`">
                        <stop offset="0%" stop-color="#666"/>
                        <stop offset="100%" stop-color="#777"/>
                    </template>
                    <template v-else-if="map.style.icons?.variant === `light`">
                        <stop offset="0%" stop-color="#ffffff"/>
                        <stop offset="100%" stop-color="#eeeeee"/>
                    </template>
                    <template v-else>
                        <stop offset="0%" stop-color="#333"/>
                        <stop offset="100%" stop-color="#000"/>
                    </template>
<!--                    <stop offset="0%" stop-color="#495b65"/>-->
<!--                    <stop offset="100%" stop-color="#61737b"/>-->
                </linearGradient>
                <linearGradient id="blue-dot-inner-gradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stop-color="#2B7FFF" />
                    <stop offset="32%" stop-color="#2B7FFF" />
                    <stop offset="100%" stop-color="#0D4EAF" />
                </linearGradient>
                <linearGradient id="purple-dot-inner-gradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stop-color="#832bff" />
                    <stop offset="32%" stop-color="#832bff" />
                    <stop offset="100%" stop-color="#4b0daf" />
                </linearGradient>
                <linearGradient id="gray-dot-inner-gradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stop-color="#adadad" />
                    <stop offset="32%" stop-color="#9c9c9c" />
                    <stop offset="100%" stop-color="#636363" />
                </linearGradient>
                <filter id="invert" color-interpolation-filters="sRGB">
                    <feComponentTransfer>
                        <!-- map 0→1 and 1→0 -->
                        <feFuncR type="table" tableValues="1 0"/>
                        <feFuncG type="table" tableValues="1 0"/>
                        <feFuncB type="table" tableValues="1 0"/>
                    </feComponentTransfer>
                </filter>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(0,0,0,0.2)" />
                </filter>
            </template>

            <slot></slot>

            <template #pointer="mouse">
                <!-- debug pointer -->
                <!-- <circle :cx="mouse.x" :cy="mouse.y" :style="{r: getScaleCSS(20)}" fill="black"></circle> -->
                <slot name="pointer" v-bind="mouse"></slot>
            </template>
        </e-interactive-svg>

        <slot name="controls"></slot>


        <div class="floor-name-container" v-if="floor && map.floors.length > 1 && showFloorName">
            <div class="floor-name">
                {{tr(floor.getName())}}
            </div>
        </div>

        <aside v-if="!fullscreen && showControls" class="map-controls-panel">
            <template  v-if="!isMobileLayoutOrPreview">
                <div class="map-control">
                    <span class="floating-label">Zoom in</span>
                    <b-btn variant="variant" @click="$refs.svg.updateZoom(10)">
                        <i class="fas fa-magnifying-glass-plus"/>
                    </b-btn>
                </div>
                <div class="map-control">
                    <span v-if="!isMobileLayoutOrPreview" class="floating-label">Zoom out</span>
                    <b-btn variant="variant" @click="$refs.svg.updateZoom(-10)">
                        <i class="fas fa-magnifying-glass-minus"/>
                    </b-btn>
                </div>
            </template>
            <div class="map-control">
                <span v-if="!isMobileLayoutOrPreview" class="floating-label">Re-center</span>
                <b-btn variant="variant" @click="$emit('reset-camera'); $refs.svg.resetView()">
                    <i class="fa-solid fa-crosshairs"/>
                </b-btn>
            </div>
            <!-- <div class="map-control">
                <b-btn variant="variant" @click="$emit('request-orientation'); $refs.svg.resetView()">
                    <i class="fa-solid fa-compass"/>
                </b-btn>
            </div> -->

            <div v-if="isDebugVisible" class="zoom-display">
                {{camera.zoomLevel}} - {{camera.zoomPercent|round}}%
            </div>
        </aside>


        <div v-else-if="isDebugVisible" class="zoom-display floating">
            {{camera.zoomLevel}} - {{camera.zoomPercent|round}}%
        </div>

        <div v-if="$slots.legend" class="map-legend">
            <slot name="legend"></slot>
        </div>
    </div>
</template>

<script>
import MapMixin from "@/helpers/maps/map-mixin.js";
import EInteractiveSvg from "@vue-components/components/e-interactive-svg.vue";


export default {
    name: `map-container`,
    mixins: [MapMixin],
    components: {EInteractiveSvg},
    props: {
        camera: {type: Object},
        itemSelected: { type: Object },
        map: { type: Object },
        floor: { type: Object },
        editable: { type: Boolean },
        fullscreen: { type: Boolean },
        preview: { type: Boolean, default: false },
        showFloorName: { type: Boolean },
        showControls: { type: Boolean },
        showScaleLegend: {type: Boolean}
    },
    data() {
        return {
        }
    },
    computed: {
        containerStyle() {
            return {
                background: this.map.style.background,
                "--map-icon-size": this.map.style.icons.size + `px`,
                "--map-ui-scale": this.map.style.uiScale,
                "--map-label-size": (12 * this.map.style.uiScale) + `px`
            };
        },
        containerClass() {
            return [
                {
                    'editable': this.editable,
                    'is-preview': this.preview,
                    'label-shadows': this.map.style.labels?.shadows
                },
                `theme-` + this.map.style.theme,
                `skin-` + this.map.style.skin,
                `icons-theme-${this.map.style.icons?.theme}`,
                `icons-variant-${this.map.style.icons?.variant}`,
                `labels-variant-${this.map.style.labels?.variant}`];
        }
    },
    methods: {
        setZoom(zoom, centerOfZoom = null) {
            this.$refs.svg.setZoom(zoom, centerOfZoom);
        }
    }
}
</script>

<style lang="scss">
.map-container {
    position: relative;
    margin-left: var(--map-container-left-padding);
    width: calc(100% - var(--map-container-left-padding));
    min-width: 300px;
    min-height: 300px;
    //background-image: linear-gradient(to top, rgb(127, 129, 82), rgb(36, 143, 171));
    background: #eee;
    overflow: hidden;
    user-select: none;

    flex: 1;
    height: calc(100dvh - var(--map-mobile-overlay-top-offset, 0px) - var(--map-mobile-overlay-bottom-offset, 0px));
    margin-top: var(--map-mobile-overlay-top-offset, 0px);
    margin-bottom: var(--map-mobile-overlay-bottom-offset, 0px);

    --map-control-size: 50px;
    --map-icon-size: 24px;
    --map-label-size: 18px;
    --map-line-height: 1.4;

    --map-primary-color: #005fe5;
    //--map-primary-color: #1b8fad;
    --map-secondary-color: #ffffff;

    .interactive-svg {
        min-height: calc(80vh);
    }

    &.is-preview {
        margin: 0;
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: 0;
        pointer-events: none;
        flex: none;

        .interactive-svg {
            min-height: 100%;
            height: 100%;
        }

        .layer-map-nav-path {
            .map-nav-path,
            .map-nav-segment {
                stroke-width: calc(5px * var(--px-to-svg));
                animation: none;
                stroke-dashoffset: 0;
            }

            .map-nav-path-dot-animation {
                display: none;
                animation: none;
            }
        }
    }

    .map-legend {
        position: absolute;
        z-index: 1;
        bottom: 50px;
        left: 0;
    }

    .map-label {
        z-index: 2;
        alignment-baseline: middle;
        user-select: none;
        pointer-events: none;

        // default is dark theme
        fill: #333 !important;
        color: #333; // iOS Safari sometimes maps CSS color → SVG fill
        stroke: white;
        font-size: var(--map-label-size);
        font-weight: 700;
        stroke-width: 3px;
        text-anchor: middle;
        paint-order: stroke;
        dominant-baseline: central;
        --text-line-index: 0;

        &.position-left {
            text-anchor: end;
            transform: translate(calc(var(--map-icon-size) * var(--icon-scale) * -0.5 - 3px), calc(var(--text-line-index) * var(--map-label-size) * var(--map-line-height)));
        }

        &.position-right {
            text-anchor: start;
            transform: translate(calc(var(--map-icon-size) * var(--icon-scale) * 0.5 + 3px), calc(var(--text-line-index) * var(--map-label-size) * var(--map-line-height)));
        }

        &.position-top {
            transform: translate(0, calc(var(--map-icon-size) * var(--icon-scale) * -0.5 - 10px - var(--text-line-index) * var(--map-label-size) * var(--map-line-height)));
        }

        &.position-auto,
        &.position-bottom {
            transform: translate(0, calc(var(--map-icon-size) * var(--icon-scale) * 0.5 + 10px + var(--text-line-index) * var(--map-label-size) * var(--map-line-height)));
        }

        .subtitle {
            font-size: calc(var(--map-label-size) * 0.9);
        }
    }

    &.label-shadows {
        .map-label {
            @supports (text-shadow: 0px 1px 0px #00000085) {
                text-shadow: 0px 1px 0px #00000085;
            }
        }
    }

    &.labels-variant-light {
        .map-label {
            fill: #fff !important;
            color: #fff; // iOS Safari sometimes maps CSS color → SVG fill
            stroke: #00000095;
        }

        &.label-shadows {
            .map-label {
                @supports (text-shadow: 0px 1px 0px #00000025) {
                    text-shadow: 0px 1px 0px #00000025;
                }
            }
        }
    }

    .floor-name-container {
        position: absolute;
        z-index: 1;
        top: 10px;
        left: 10px;
        pointer-events: none;

        .floor-name {
            background: rgba(#000, 0.9);
            color: white;
            border-radius: 50px;
            padding: 10px 20px;
        }
    }

    .map-controls-panel {
        position: absolute;
        bottom: calc(20px + var(--page-bottom-margin));
        right: 10px;
        text-align: right;

        &:hover span {
            opacity: 1;
            margin-right: 0;
        }

        .floating-label {
            display: inline-block;
            opacity: 0;
            margin-right: 10px;
            font-weight: 500;
            text-align: right;
            transition: opacity 250ms, margin-right 250ms;
            pointer-events: none;
            color: black;
            text-shadow: 2px 2px 5px white;
        }

        .btn {
            display: inline-block;
            width: var(--map-control-size);
            height: var(--map-control-size);
            font-size: x-large;
            margin: 5px 0;
            background-color: #fff;
            color: #333;
            box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);

            &:hover {
                background-color: rgb(36, 94, 117);
                color: white;
            }

            &:active {
                background-color: rgb(36, 94, 117);
                color: white;
            }

            &:focus, &:visited {
                background-color: rgb(36, 94, 117);
                color: white;
            }

            &.btn-toggled {
                color: white;
                background-color: #369676;
            }
        }
    }

    .zoom-display {
        position: absolute;
        top: -30px;
        right: 0;
        background: white;
        font-size: 10pt;
        font-weight: 500;
        white-space: nowrap;
        color: #333;
        padding: 5px 10px;
        border-radius: 10px;

        &.floating {
            top: 73px;
            left: 5px;
            right: auto;
        }
    }

    --selection-color: 255, 165, 0;

    .item-selected {
        &.type-shelf,
        &.zone {
            rect {
                fill: rgba(255, 210, 4, 0.2);
                stroke: rgb(var(--selection-color));
            }

            text {
                fill: #cf8600;
            }
        }

        circle.selector {
            fill: rgba(255, 182, 0, 0.3) !important;
            stroke: rgb(var(--selection-color)) !important;
        }

        .radius {
            fill: rgba(255, 182, 0, 0.1);
            stroke: rgba(255, 165, 0, 0.5);

            &.short-range {
                fill: rgba(255, 182, 0, 0.2);
                stroke: var(--selection-color);
            }
        }
    }
}

.layout-mobile {
    .map-container {
        .map-controls-panel {
            bottom: calc(150px + var(--page-bottom-margin));
            top: auto;
            --map-control-size: 40px;

            .btn {
                display: inline-block;
                padding: 0;
                border-radius: 50%;
                font-size: 1.2rem;
            }
        }
    }
}

.layout-kiosk {
    .map-container {
        height: var(--kiosk-map-height) !important;
    }

    .map-controls-panel {
        left: 20px;
        right: auto;
        bottom: 120px;

        .floating-label {
            display: inline-block;
            position: absolute;
            left: 60px;
            width: 150px;
            text-align: left;
            line-height: var(--map-control-size);
            margin-right: 10px;
        }
    }

    .floor-name-container {
        //left: 0;
        //width: 100%;
        //pointer-events: none;
        //display: flex;
        //align-items: center;
        //justify-content: center;

        .floor-name {
            font-size: 16pt;
        }
    }
}
</style>
